import { PreviewDataset } from '@/components/PreviewDataset';
import {
  uploadFileToS3,
  useCompleteDataVersionUpload,
  useCreateDataVersion,
} from '@/queries/dataset-data-versions';
import { useCreateDatasetSchemaVersion } from '@/queries/dataset-schema-versions';
import {
  useCreateDataset,
  useDatasets,
  useUpdateDataset,
} from '@/queries/datasets';
import type { DatasetDetail } from '@/queries/datasets';
import { getErrorMessage } from '@/utils';
import CheckCircleOutlined from '@ant-design/icons/CheckCircleOutlined';
import CloseCircleOutlined from '@ant-design/icons/CloseCircleOutlined';
import CloudUploadOutlined from '@ant-design/icons/CloudUploadOutlined';
import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import FileExcelOutlined from '@ant-design/icons/FileExcelOutlined';
import FileTextOutlined from '@ant-design/icons/FileTextOutlined';
import QuestionCircleOutlined from '@ant-design/icons/QuestionCircleOutlined';
import SettingOutlined from '@ant-design/icons/SettingOutlined';
import ThunderboltOutlined from '@ant-design/icons/ThunderboltOutlined';
import App from 'antd/es/app';
import Button from 'antd/es/button';
import Input from 'antd/es/input';
import Space from 'antd/es/space';
import Tooltip from 'antd/es/tooltip';
import Typography from 'antd/es/typography';
import Upload from 'antd/es/upload';
import type { RcFile, UploadFile } from 'antd/es/upload';
import { useMemo, useState } from 'react';
import { useTheme } from 'styled-components';
import { useDebounce } from 'use-debounce';

import {
  ActionButton,
  FileDisplayContainer,
  FileIcon,
  FileName,
  FileSize,
  FormActions,
  OptionCard,
  RemoveButton,
  StatusContainer,
  UploadContainer,
} from './CreateDataset.styles';
import {
  createSchemaFromPreview,
  dataPreviewFromFile,
  detectDelimiter,
} from './CreateDataset.utils';
import { ProgressContent } from './components/ProgressContent';

const { Text } = Typography;
const { Dragger } = Upload;

type UploadMode = 'smart' | 'manual';

type SupportedFileType = 'csv' | 'xlsx';

type FormSteps = 'upload' | 'preview' | 'progress' | 'success' | 'error';

export const SuccessContent = () => {
  const theme = useTheme();

  return (
    <StatusContainer>
      <CheckCircleOutlined
        style={{
          fontSize: `${theme.fontSizeHeading1 + 10}px`,
          color: theme.colorSuccess,
        }}
      />
      <Text
        style={{
          fontSize: `${theme.fontSizeLG}px`,
          marginTop: `${theme.margin}px`,
          display: 'block',
          color: theme.colorSuccess,
        }}
      >
        Upload successful!
      </Text>
      <Text type="secondary">
        Your data has been uploaded and is ready to use
      </Text>
    </StatusContainer>
  );
};

export const ErrorContent = ({ errorMessage }: { errorMessage: string }) => {
  const theme = useTheme();

  return (
    <StatusContainer>
      <CloseCircleOutlined
        style={{
          fontSize: `${theme.fontSizeHeading1 + 10}px`,
          color: theme.colorError,
        }}
      />
      <Text
        style={{
          fontSize: `${theme.fontSizeLG}px`,
          marginTop: `${theme.margin}px`,
          display: 'block',
          color: theme.colorError,
        }}
      >
        Upload failed
      </Text>
      <Text type="danger">{errorMessage}</Text>
    </StatusContainer>
  );
};

export const CreateDatasetForm = ({
  onCancel,
  onComplete,
  teamId,
}: {
  onCancel?: () => void;
  onComplete?: (dataset: DatasetDetail) => void;
  teamId: string;
}) => {
  const { message } = App.useApp();
  const createDatasetMutation = useCreateDataset();
  const theme = useTheme();

  // Form state
  const [datasetName, setDatasetName] = useState<string>('');

  // Upload state
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [fileType, setFileType] = useState<SupportedFileType>('csv');
  const [uploadMode, setUploadMode] = useState<UploadMode>('smart');
  const [formStep, setFormStep] = useState<FormSteps>('upload');

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Mutations for manual upload
  const createDataVersionMutation = useCreateDataVersion();
  const completeUploadMutation = useCompleteDataVersionUpload();
  const createSchemaVersionMutation = useCreateDatasetSchemaVersion();
  const updateDatasetMutation = useUpdateDataset();

  // Query for conflicting file names
  const [debouncedDatasetName] = useDebounce(datasetName, 500);
  const conflictingDatasetNamesQuery = useDatasets(
    {
      name: debouncedDatasetName,
      teamIds: [teamId],
      pageSize: 1,
    },
    {
      enabled: !!debouncedDatasetName && debouncedDatasetName.length > 0,
      keepPreviousData: false,
    },
  );
  const hasConflictingDatasetNames = (
    conflictingDatasetNamesQuery.data?.items ?? []
  ).reduce((prev, curr) => {
    if (prev) {
      return prev;
    }
    return curr.name === debouncedDatasetName;
  }, false);

  const handleFileChange = (info: { fileList: UploadFile[] }) => {
    // Keep only the latest file
    const latestFile = info.fileList.slice(-1);
    setFileList(latestFile);

    // Auto-detect file type from extension
    if (latestFile.length > 0 && latestFile[0]?.name) {
      const fileName = latestFile[0].name.toLowerCase();
      if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
        setFileType('xlsx');
      } else if (fileName.endsWith('.csv')) {
        setFileType('csv');
      }
    }
  };

  const beforeUpload = (file: RcFile) => {
    const isValidType =
      file.type === 'text/csv' ||
      file.type ===
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      file.type === 'application/vnd.ms-excel' ||
      file.name.endsWith('.csv') ||
      file.name.endsWith('.xlsx') ||
      file.name.endsWith('.xls');

    if (!isValidType) {
      void message.error('You can only upload CSV or Excel files!');
      return false;
    }

    const isLt100M = file.size / 1024 / 1024 < 100;
    if (!isLt100M) {
      void message.error('File must be smaller than 100MB!');
      return false;
    }

    return false; // Prevent automatic upload
  };

  const handleManualUpload = async () => {
    if (!fileList[0]?.originFileObj) {
      void message.error('Please select a file to upload');
      return;
    }

    try {
      const file = fileList[0].originFileObj as RcFile;

      // Default dataset name
      const defaultDatasetName = file.name.replace(/\.[^/.]+$/, '');

      // Step 0 - Create dataset
      const dataset = await createDatasetMutation.mutateAsync({
        name: datasetName.trim() || defaultDatasetName,
        teamId,
      });

      // Step 1 - Create data version and get presigned URL
      const createResponse = await createDataVersionMutation.mutateAsync({
        datasetId: dataset.datasetId,
        fileName: file.name,
        fileType,
        status: 'current',
      });

      // Step 2 - Upload file to S3 using presigned URL
      await uploadFileToS3(createResponse.uploadUrl, file);

      // Step 3 - Mark upload as complete
      await completeUploadMutation.mutateAsync({
        dataVersionId: createResponse.dataVersion.datasetDataVersionId,
        data: {},
      });

      setFormStep('success');
    } catch (error) {
      const errorMessage = getErrorMessage(error, 'Failed to upload data');
      setErrorMessage(errorMessage);
      setFormStep('error');
    }
  };

  const handleSmartUpload = async () => {
    if (!fileList[0]?.originFileObj) {
      void message.error('Please select a file to upload');
      return;
    }

    try {
      const file = fileList[0].originFileObj as RcFile;

      const preview = await dataPreviewFromFile(file, fileType);

      const defaultDatasetName = file.name.replace(/\.[^/.]+$/, '');

      // Step 0 - Create dataset
      const dataset = await createDatasetMutation.mutateAsync({
        name: datasetName.trim() || defaultDatasetName,
        teamId,
      });

      // Step 1 - Create schema version first
      const fileText = await file.text();
      const detectedDelimiter = detectDelimiter(fileText);
      const schemaData = createSchemaFromPreview(
        preview,
        dataset.datasetId,
        detectedDelimiter,
      );

      await createSchemaVersionMutation.mutateAsync(schemaData);

      // Step 2 - Create data version and get presigned URL
      const createResponse = await createDataVersionMutation.mutateAsync({
        datasetId: dataset.datasetId,
        fileName: file.name,
        fileType: fileType,
        status: 'current',
      });

      // Step 3 - Upload file to S3 using presigned URL
      await uploadFileToS3(createResponse.uploadUrl, file);

      // Step 4 - Mark upload as complete
      await completeUploadMutation.mutateAsync({
        dataVersionId: createResponse.dataVersion.datasetDataVersionId,
        data: {},
      });

      // Step 6 - Auto-trigger processing for Smart Upload
      await updateDatasetMutation.mutateAsync({
        datasetId: dataset.datasetId,
        data: { shouldProcess: true },
      });

      setFormStep('success');
    } catch (error) {
      const errorMessage = getErrorMessage(error, 'Failed to upload data');
      setErrorMessage(errorMessage);
      setFormStep('error');
    }
  };

  const handleNext = async () => {
    // Initial step where user can select mode and upload file
    if (formStep === 'upload') {
      if (uploadMode === 'smart') {
        setFormStep('preview');
        return;
      } else {
        setFormStep('progress');
        await handleManualUpload();
        return;
      }
    }

    // If in preview state && smart upload, start upload
    if (formStep === 'preview' && uploadMode === 'smart') {
      setFormStep('progress');
      await handleSmartUpload();
      return;
    }

    // If in success state, close the modal
    if (formStep === 'success') {
      onCancel?.();
      return;
    }

    if (fileList.length === 0) {
      void message.error('Please select a file to upload');
      return;
    }

    // If no dataset name is provided, use the filename as fallback
    const currentName = datasetName.trim();
    if (!currentName && fileList[0]?.name) {
      const fileName = fileList[0].name;
      // Remove file extension for cleaner dataset name
      const datasetName = fileName.replace(/\.[^/.]+$/, '');
      setDatasetName(datasetName);
    }

    // For manual upload, create dataset and start upload immediately
    try {
      const dataset = await createDatasetMutation.mutateAsync({
        name: datasetName.trim(),
        teamId,
      });
      await handleManualUpload();
      onComplete?.(dataset);
    } catch (error) {
      const errorMessage = getErrorMessage(error, 'Failed to create dataset');
      void message.error(errorMessage);
    }
  };

  const handleModeChange = (mode: UploadMode) => {
    setUploadMode(mode);
  };

  const renderFileDisplay = () => {
    if (fileList.length === 0 || !fileList[0]) return null;

    const file = fileList[0];
    const fileSizeMB = (file.size || 0) / 1024 / 1024;

    return (
      <FileDisplayContainer>
        <Space direction="vertical" size="small">
          <FileIcon>
            {fileType === 'xlsx' ? <FileExcelOutlined /> : <FileTextOutlined />}
          </FileIcon>
          <FileName strong>{file.name}</FileName>
          <FileSize type="secondary">({fileSizeMB.toFixed(1)} MB)</FileSize>
          <RemoveButton
            type="text"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setFileList([]);
            }}
            icon={<DeleteOutlined />}
          />
        </Space>
      </FileDisplayContainer>
    );
  };

  const renderUploadContent = () => {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: `${theme.marginSM}px`,
        }}
      >
        <Input
          placeholder="Enter a name for your dataset (optional)"
          value={datasetName}
          onChange={(e) => {
            setDatasetName(e.target.value);
          }}
        />
        {hasConflictingDatasetNames && (
          <Text type="danger">
            A dataset with this name already exists. Please choose a different
            name.
          </Text>
        )}

        <div>
          <OptionCard
            $selected={uploadMode === 'smart'}
            onClick={() => handleModeChange('smart')}
            size="small"
          >
            <Space>
              <ThunderboltOutlined style={{ color: theme.colorPrimary }} />
              <div>
                <Text strong>Smart Upload</Text>
                <Tooltip title="Automatically detects your data structure, creates schema, and processes it for immediate querying">
                  <QuestionCircleOutlined
                    style={{
                      marginLeft: `${theme.marginXXS}px`,
                      color: theme.colorTextTertiary,
                    }}
                  />
                </Tooltip>
                <br />
                <Text
                  type="secondary"
                  style={{ fontSize: `${theme.fontSizeSM}px` }}
                >
                  Auto-detects columns and types • Instant preview •
                  Auto-processing
                </Text>
              </div>
            </Space>
          </OptionCard>

          <OptionCard
            $selected={uploadMode === 'manual'}
            onClick={() => handleModeChange('manual')}
            size="small"
          >
            <Space>
              <SettingOutlined style={{ color: theme.colorTextSecondary }} />
              <div>
                <Text strong>Manual Upload</Text>
                <Tooltip title="Traditional upload process with manual configuration">
                  <QuestionCircleOutlined
                    style={{
                      marginLeft: `${theme.marginXXS}px`,
                      color: theme.colorTextTertiary,
                    }}
                  />
                </Tooltip>
                <br />
                <Text
                  type="secondary"
                  style={{ fontSize: `${theme.fontSizeSM}px` }}
                >
                  Full control • Custom configuration • Advanced options
                </Text>
              </div>
            </Space>
          </OptionCard>
        </div>

        <UploadContainer>
          <Dragger
            fileList={fileList}
            onChange={handleFileChange}
            beforeUpload={beforeUpload}
            maxCount={1}
            accept=".csv,.xlsx,.xls"
            showUploadList={false}
          >
            {fileList.length > 0 && fileList[0] ? (
              renderFileDisplay()
            ) : (
              <>
                <p className="ant-upload-drag-icon">
                  <CloudUploadOutlined
                    style={{ fontSize: `${theme.fontSizeHeading1 + 10}px` }}
                  />
                </p>
                <p className="ant-upload-text">Upload your File</p>
                <p className="ant-upload-hint">
                  Drag and Drop or Select from Computer
                </p>
              </>
            )}
          </Dragger>
        </UploadContainer>
      </div>
    );
  };

  const renderPreviewContent = () => {
    return (
      <PreviewDataset
        file={fileList[0].originFileObj as RcFile}
        fileType={fileType}
      />
    );
  };

  const renderProgressContent = () => {
    return <ProgressContent />;
  };

  const renderSuccessContent = () => {
    return <SuccessContent />;
  };

  const renderErrorContent = () => {
    return <ErrorContent errorMessage={errorMessage || ''} />;
  };

  const renderContent = () => {
    if (formStep === 'upload') {
      return renderUploadContent();
    } else if (formStep === 'preview') {
      return renderPreviewContent();
    } else if (formStep === 'progress') {
      return renderProgressContent();
    } else if (formStep === 'success') {
      return renderSuccessContent();
    } else if (formStep === 'error') {
      return renderErrorContent();
    }
    return null;
  };

  const handleBack = () => {
    if (formStep === 'preview') {
      setFormStep('upload');
    }
    if (formStep === 'error') {
      setFormStep('upload');
    }
  };

  const backButtonDisabled =
    formStep === 'upload' || formStep === 'success' || formStep === 'progress';

  const nextButtonDisabled =
    formStep === 'progress' ||
    (formStep === 'upload' &&
      (fileList.length === 0 || hasConflictingDatasetNames)) ||
    formStep === 'error';

  const cancelButtonDisabled =
    formStep === 'progress' || formStep === 'success';

  const uploadButtonText = useMemo(() => {
    if (formStep === 'upload') {
      return uploadMode === 'smart' ? 'Next' : 'Upload';
    }
    if (formStep === 'success') {
      return 'Close';
    }
    return 'Next';
  }, [formStep, uploadMode]);

  return (
    <>
      {renderContent()}

      <FormActions>
        <Button onClick={onCancel} disabled={cancelButtonDisabled}>
          Cancel
        </Button>
        <ActionButton
          onClick={() => handleBack()}
          disabled={backButtonDisabled}
        >
          Back
        </ActionButton>
        <ActionButton
          type="primary"
          htmlType="button"
          onClick={handleNext}
          disabled={nextButtonDisabled}
        >
          {uploadButtonText}
        </ActionButton>
      </FormActions>
    </>
  );
};

export default CreateDatasetForm;
